import { useEffect, useState, type ReactNode } from 'react'
import styled from '@emotion/styled/macro'
import { Dialog, DialogContent, Input } from '@material-ui/core'
import { ActionButton, StyledDialogActions, StyledDialogTitle, StyledInputLabel } from 'components/widgets'
import { GRAY3_COLOR } from 'lib/constants'
import { trans, TranslationGroup } from 'lib/types'

// The shared `StyledInput` widget is padded for the full-width login and reset
// forms; at dialog scale it renders a box several times taller than its one line
// of text. This is the compact treatment the other modals use.
const ConfirmInput = styled(Input)({
  background: GRAY3_COLOR,
  borderRadius: '10px',
  padding: '4px 12px',
  fontFamily: 'Montserrat',
  fontSize: '1.4rem',
})

type Props = {
  open: boolean
  title: string
  /** Name the record being destroyed. This is the operator's last chance to read it. */
  description?: ReactNode
  /**
   * Typed back before the destructive button unlocks. Pass the record's own name:
   * a fixed word like "DELETE" proves intent but not aim, and reproducing the name
   * is what forces the operator to look at which record they actually picked.
   * Compared case-sensitively, ignoring leading and trailing space on both sides.
   */
  confirmPhrase: string
  /** Destructive button label. Defaults to the common "delete". */
  confirmLabel?: string
  onConfirm: () => void
  handleClose: () => void
}

/**
 * A confirmation that a reflex can't clear: the operator has to reproduce
 * `confirmPhrase` before the destructive button enables.
 *
 * Reach for this only where the action is irreversible and a mis-click costs
 * real data. `ConfirmationDialog` remains the right control for anything the
 * user can simply redo — spending this much friction on those trains people to
 * type the phrase without reading the dialog, which is the failure it exists to
 * prevent.
 */
export const TypeToConfirmDialog = ({
  open,
  title,
  description,
  confirmPhrase,
  confirmLabel,
  onConfirm,
  handleClose,
}: Props) => {
  const common: TranslationGroup = trans.common()
  const [typed, setTyped] = useState('')

  // Every open starts empty. A phrase left behind by an abandoned attempt would
  // arm the button before the operator had read which record is now in the title.
  useEffect(() => setTyped(''), [open])

  // A blank phrase leaves nothing to reproduce, so an empty box would satisfy the
  // comparison and hand back a one-click delete. An unnamed record fails closed
  // instead: the button never arms, and the operator renames it or deletes it from
  // somewhere that can identify it.
  const required = confirmPhrase.trim()
  const armed = !!required && typed.trim() === required

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>{title}</StyledDialogTitle>
      <DialogContent>
        {description}
        <div style={{ marginTop: '20px', marginBottom: '8px' }}>
          <StyledInputLabel htmlFor="type-to-confirm">
            {common.type_to_confirm} <strong>{required}</strong>
          </StyledInputLabel>
        </div>
        <ConfirmInput
          id="type-to-confirm"
          value={typed}
          onChange={e => setTyped(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && armed) onConfirm()
          }}
          autoFocus
          autoComplete="off"
          disableUnderline
          fullWidth
        />
      </DialogContent>
      <StyledDialogActions>
        <ActionButton width="110px" height="28px" onClick={handleClose}>
          {common.cancel}
        </ActionButton>
        <ActionButton width="110px" height="28px" error onClick={onConfirm} disabled={!armed}>
          {confirmLabel || common.delete}
        </ActionButton>
      </StyledDialogActions>
    </Dialog>
  )
}
